import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Minus, Plus, Loader2, ArrowLeft, Check, Clock, Backpack } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatISK, formatDate, formatApiErrorDetail } from "@/lib/api";
import CancellationPolicy from "@/components/CancellationPolicy";
import PhoneInput from "@/components/PhoneInput";

export default function BuggyBooking() {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [availability, setAvailability] = useState({});
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [riderType, setRiderType] = useState("double");
  const [buggies, setBuggies] = useState(1);
  const [customer, setCustomer] = useState({ full_name: "", email: "", phone: "", allergies: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api.get("/buggy/availability").then((res) => {
      setInfo(res.data);
      setAvailability(res.data.availability);
    }).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  if (!info) {
    return <div className="min-h-[60vh] flex items-center justify-center text-gray-500 text-xs uppercase tracking-[0.3em]">Loading…</div>;
  }

  const pricePerBuggy = riderType === "single" ? info.single_price : info.double_price * 2;
  const ridersPerBuggy = riderType === "single" ? 1 : 2;
  const pax = buggies * ridersPerBuggy;
  const total = buggies * pricePerBuggy;
  const feePercent = info.card_fee_percent || 0;
  const cardFee = Math.round(total * feePercent / 100);
  const grandTotal = total + cardFee;
  const remaining = date && slot && availability[date] ? availability[date][slot].remaining : null;

  const submit = async (e) => {
    e.preventDefault();
    if (!date) return toast.error("Choose a date");
    if (!slot) return toast.error("Choose a time slot");
    if (!customer.full_name || !customer.email || !customer.phone) return toast.error("Please complete your details");
    setSubmitting(true);
    try {
      const res = await api.post("/bookings/buggy", { date, slot, rider_type: riderType, buggies, customer });
      const checkout = await api.post("/payments/checkout", {
        booking_id: res.data.booking.id, origin_url: window.location.origin,
      });
      window.location.href = checkout.data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Booking failed");
      load();
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="buggy-booking-page">
      <div className="relative h-[42vh] min-h-[320px] overflow-hidden">
        <img src="https://static.prod-images.emergentagent.com/jobs/44292365-21c1-4f3d-88e3-fd6ad71c5275/images/5677bb34e8db9262d800c596c1adfa00ad58abfc19cb411d70f50b4a1f860e2c.png" alt="Buggy tour" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-[#050505]/20" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 h-full flex flex-col justify-end pb-10">
          <Link to="/tours" className="text-xs uppercase tracking-[0.2em] text-gray-300 hover:text-white inline-flex items-center gap-2 mb-5"><ArrowLeft size={14} /> All tours</Link>
          <p className="text-xs uppercase tracking-[0.3em] text-gray-300 mb-4 inline-flex items-center gap-2"><Clock size={14} /> {info.duration} · Viking Village</p>
          <h1 className="font-display font-extrabold uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.92] max-w-3xl">Buggy Tour</h1>
        </div>
      </div>

      <form onSubmit={submit} className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-12">
          {/* Includes */}
          <section className="grid sm:grid-cols-2 gap-4">
            {info.bring && (
              <div data-testid="buggy-bring" className="border border-white/10 bg-[#0C0C0E] p-5">
                <p className="text-xs uppercase tracking-[0.15em] text-gray-500 mb-2 inline-flex items-center gap-2"><Backpack size={15} /> What to bring</p>
                <p className="text-sm text-gray-300 leading-relaxed">{info.bring}</p>
              </div>
            )}
            <div data-testid="buggy-includes" className="border border-white/10 bg-[#0C0C0E] p-5">
              <p className="text-xs uppercase tracking-[0.15em] text-gray-500 mb-2 inline-flex items-center gap-2"><Check size={15} /> What's included</p>
              <p className="text-sm text-gray-300 leading-relaxed">{info.includes_text || info.includes.join(", ")}</p>
            </div>
          </section>

          {/* Date */}
          <section>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-5">01 — Select Date</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {info.dates.map((d) => (
                <button key={d} type="button" data-testid={`buggy-date-${d}`} onClick={() => { setDate(d); setSlot(""); }}
                  className={`py-4 px-3 border text-center transition-all ${date === d ? "bg-white text-black border-white" : "border-white/15 text-gray-300 hover:border-white/50"}`}>
                  <span className="block text-xs uppercase tracking-[0.1em]">{formatDate(d)}</span>
                </button>
              ))}
            </div>
          </section>

          {/* Slots */}
          <section>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-5">02 — Time Slot</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {info.slots.map((s) => {
                const cell = date && availability[date] ? availability[date][s] : null;
                if (cell && cell.closed) return null;
                const rem = cell ? cell.remaining : info.capacity;
                const soldOut = date && rem <= 0;
                return (
                  <button key={s} type="button" disabled={soldOut} data-testid={`buggy-slot-${s}`} onClick={() => setSlot(s)}
                    className={`py-5 px-3 border text-center transition-all disabled:opacity-40 disabled:cursor-not-allowed ${slot === s ? "bg-white text-black border-white" : "border-white/15 text-gray-300 hover:border-white/50"}`}>
                    <span className="block font-display font-bold text-xl">{s}</span>
                    <span className="block text-[10px] uppercase tracking-[0.12em] mt-2 opacity-70">
                      {date ? (soldOut ? "Sold out" : `${rem} buggies left`) : "Select date first"}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="mt-4 text-[11px] leading-relaxed text-gray-500 italic max-w-xl">
              Estimate departure time. Exact departure times will be confirmed closer to the event and may change due to weather or operational conditions.
            </p>
          </section>

          {/* Rider type */}
          <section>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-5">03 — Rider Setup</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button type="button" data-testid="rider-double" onClick={() => setRiderType("double")}
                className={`p-6 border text-left transition-all ${riderType === "double" ? "bg-white text-black border-white" : "border-white/15 text-gray-300 hover:border-white/50"}`}>
                <span className="block font-display font-bold text-lg uppercase tracking-tight">Double Rider</span>
                <span className="block text-xs mt-2 opacity-70">2 riders share a buggy · {formatISK(info.double_price)} each</span>
                <span className="block text-sm mt-3 font-semibold">{formatISK(info.double_price * 2)} / buggy</span>
              </button>
              <button type="button" data-testid="rider-single" onClick={() => setRiderType("single")}
                className={`p-6 border text-left transition-all ${riderType === "single" ? "bg-white text-black border-white" : "border-white/15 text-gray-300 hover:border-white/50"}`}>
                <span className="block font-display font-bold text-lg uppercase tracking-tight">Single Rider</span>
                <span className="block text-xs mt-2 opacity-70">The whole buggy to yourself</span>
                <span className="block text-sm mt-3 font-semibold">{formatISK(info.single_price)} / buggy</span>
              </button>
            </div>
          </section>

          {/* Buggies count */}
          <section>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">04 — Number of Buggies</p>
            <div className="flex items-center justify-between py-4 border-b border-white/10">
              <div>
                <p className="text-sm uppercase tracking-[0.1em]">Buggies</p>
                <p className="text-xs text-gray-500 mt-1">{pax} rider{pax > 1 ? "s" : ""} total</p>
              </div>
              <div className="flex items-center gap-5">
                <button type="button" data-testid="buggy-minus" onClick={() => setBuggies(Math.max(1, buggies - 1))} disabled={buggies <= 1} className="w-9 h-9 border border-white/20 hover:border-white/60 flex items-center justify-center transition-colors disabled:opacity-30"><Minus size={15} /></button>
                <span data-testid="buggy-value" className="w-6 text-center font-display font-bold text-lg">{buggies}</span>
                <button type="button" data-testid="buggy-plus" onClick={() => setBuggies(buggies + 1)} className="w-9 h-9 border border-white/20 hover:border-white/60 flex items-center justify-center transition-colors"><Plus size={15} /></button>
              </div>
            </div>
            {remaining !== null && buggies > remaining && (
              <p data-testid="buggy-capacity-warning" className="text-[#C44536] text-xs uppercase tracking-[0.1em] mt-3">Only {Math.max(remaining, 0)} buggies left for this slot</p>
            )}
          </section>

          {/* Customer */}
          <section>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-6">05 — Your Details</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7">
              <div>
                <label className="block text-xs uppercase tracking-[0.1em] text-gray-400 mb-2">Full name</label>
                <input data-testid="buggy-customer-name" required value={customer.full_name} onChange={(e) => setCustomer({ ...customer, full_name: e.target.value })} className="bg-transparent border-b border-white/20 px-0 py-3 text-white focus:outline-none focus:border-white transition-colors w-full" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.1em] text-gray-400 mb-2">Email</label>
                <input type="email" data-testid="buggy-customer-email" required value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="bg-transparent border-b border-white/20 px-0 py-3 text-white focus:outline-none focus:border-white transition-colors w-full" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.1em] text-gray-400 mb-2">Phone</label>
                <PhoneInput testid="buggy-customer-phone" required onChange={(v) => setCustomer((prev) => ({ ...prev, phone: v }))} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.1em] text-gray-400 mb-2">Notes (optional)</label>
                <input data-testid="buggy-customer-notes" value={customer.notes} onChange={(e) => setCustomer({ ...customer, notes: e.target.value })} className="bg-transparent border-b border-white/20 px-0 py-3 text-white focus:outline-none focus:border-white transition-colors w-full" />
              </div>
            </div>
          </section>
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="lg:sticky lg:top-28 bg-[#0C0C0E] border border-white/10 p-8">
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-6">Summary</p>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between text-gray-400"><span>Date</span><span className="text-white">{date ? formatDate(date) : "—"}</span></div>
              <div className="flex justify-between text-gray-400"><span>Time</span><span className="text-white">{slot || "—"}</span></div>
              <div className="flex justify-between text-gray-400"><span>Setup</span><span className="text-white capitalize">{riderType} rider</span></div>
              <div className="flex justify-between text-gray-400"><span>{formatISK(pricePerBuggy)} × {buggies}</span><span className="text-white">{formatISK(total)}</span></div>
              <div className="flex justify-between text-gray-400"><span>Riders</span><span className="text-white">{pax}</span></div>
            </div>
            <div className="mt-6 pt-6 border-t border-white/10">
              <span className="text-xs uppercase tracking-[0.15em] text-gray-400 block">Total</span>
              <span data-testid="buggy-total" className="font-display font-extrabold text-3xl block mt-1">{formatISK(grandTotal)}</span>
            </div>
            {cardFee > 0 && (
              <div className="flex justify-between text-[11px] text-gray-500 mt-3">
                <span data-testid="buggy-fee-label">Incl. {feePercent}% card fee</span>
                <span data-testid="buggy-fee-amount">{formatISK(cardFee)}</span>
              </div>
            )}
            <button type="submit" data-testid="confirm-buggy-booking" disabled={submitting} className="w-full mt-8 bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-4 transition-colors inline-flex items-center justify-center gap-3 disabled:opacity-60">
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Redirecting to payment</> : "Pay & Confirm"}
            </button>
          </motion.div>
          <CancellationPolicy className="mt-6" />
        </div>
      </form>
    </div>
  );
}
